import { ICE_SERVERS } from "@/constants";
import { debugError, debugLog } from "@/lib/logger";
import { setStreamingActive } from "@/lib/streamingState";
import type { ConnectionState } from "@/types";
import type { PeerConnectionInfo } from "./types";

type SignalingSender = {
  send: (message: unknown) => void;
};

export async function flushPendingIceCandidates(
  pc: RTCPeerConnection,
  candidates: RTCIceCandidateInit[],
): Promise<void> {
  if (candidates.length === 0 || !pc.remoteDescription) {
    return;
  }

  debugLog(`[WebRTC] Flushing ${candidates.length} pending ICE candidates`);

  for (const candidate of candidates) {
    try {
      await pc.addIceCandidate(new RTCIceCandidate(candidate));
    } catch (error) {
      debugError("[WebRTC] Failed to add buffered ICE candidate", error);
    }
  }
}

export function syncBabyPeerConnectionTracks(
  connections: Map<string, PeerConnectionInfo>,
  localStream: MediaStream,
): void {
  const videoTrack = localStream.getVideoTracks()[0];
  const audioTrack = localStream.getAudioTracks()[0];

  connections.forEach((info, parentId) => {
    const senders = info.pc.getSenders();
    const transceivers = info.pc.getTransceivers();

    if (videoTrack) {
      let videoSender = senders.find((sender) => sender.track?.kind === "video");

      if (!videoSender) {
        const videoTransceiver = transceivers.find(
          (transceiver) =>
            transceiver.sender.track === null ||
            transceiver.sender.track?.kind === "video" ||
            transceiver.mid === "0",
        );
        if (videoTransceiver) {
          videoSender = videoTransceiver.sender;
        }
      }

      if (videoSender) {
        const needsReplace =
          !videoSender.track || videoSender.track.id !== videoTrack.id;
        if (needsReplace) {
          debugLog(`[WebRTC/Baby] Replacing video track for ${parentId}`);
          videoSender.replaceTrack(videoTrack).catch((error) => {
            debugError("[WebRTC/Baby] Failed to replace video track", error);
          });
        }
      } else {
        debugLog(`[WebRTC/Baby] No video sender found for ${parentId}`);
      }
    }

    if (audioTrack) {
      let audioSender = senders.find((sender) => sender.track?.kind === "audio");

      if (!audioSender) {
        const audioTransceiver = transceivers.find(
          (transceiver) =>
            transceiver.sender.track?.kind === "audio" ||
            transceiver.mid === "1",
        );
        if (audioTransceiver) {
          audioSender = audioTransceiver.sender;
        }
      }

      if (audioSender) {
        const isSameTrack =
          audioSender.track === audioTrack ||
          (audioSender.track && audioSender.track.id === audioTrack.id);

        if (!isSameTrack) {
          debugLog(`[WebRTC/Baby] Replacing audio track for ${parentId}`);
          audioSender.replaceTrack(audioTrack).catch((error) => {
            debugError("[WebRTC/Baby] Failed to replace audio track", error);
          });
        }
      }
    }
  });
}

export function updateBabyConnectionState(args: {
  role: "baby" | "parent";
  connections: Map<string, PeerConnectionInfo>;
  setConnectedPeersCount: (count: number) => void;
  setConnectionState: (state: ConnectionState) => void;
}) {
  if (args.role !== "baby") {
    return;
  }

  let count = 0;
  args.connections.forEach((info) => {
    if (info.pc.connectionState === "connected") {
      count++;
    }
  });

  args.setConnectedPeersCount(count);
  setStreamingActive(count > 0);

  if (count > 0) {
    args.setConnectionState("connected");
  } else if (args.connections.size > 0) {
    args.setConnectionState("connecting");
  } else {
    args.setConnectionState("idle");
  }
}

export function createBabyPeerConnection(args: {
  parentId: string;
  localStream: MediaStream | null;
  peerConnectionsMap: Map<string, PeerConnectionInfo>;
  signalingClient: SignalingSender;
  onParentAudioTrack?: (track: MediaStreamTrack, stream: MediaStream) => void;
  updateConnectedPeersCount: () => void;
}): RTCPeerConnection {
  const { parentId } = args;
  debugLog(`[WebRTC/Baby] Creating peer connection for parent: ${parentId}`);

  const pc = new RTCPeerConnection(ICE_SERVERS);
  args.peerConnectionsMap.set(parentId, {
    pc,
    pendingIceCandidates: [],
  });

  if (args.localStream) {
    const tracks = args.localStream.getTracks();
    tracks.forEach((track) => {
      pc.addTrack(track, args.localStream!);
    });
    debugLog(
      `[WebRTC/Baby] Added ${tracks.length} tracks for parent ${parentId}`,
    );
  }

  pc.onicecandidate = (event) => {
    if (event.candidate) {
      debugLog(`[WebRTC/Baby] Sending ICE candidate to parent ${parentId}`);
      args.signalingClient.send({
        type: "ice",
        targetId: parentId,
        candidate: event.candidate,
      });
    }
  };

  pc.onconnectionstatechange = () => {
    debugLog(
      `[WebRTC/Baby] Connection state for ${parentId}: ${pc.connectionState}`,
    );

    if (
      pc.connectionState === "disconnected" ||
      pc.connectionState === "failed" ||
      pc.connectionState === "closed"
    ) {
      debugLog(`[WebRTC/Baby] Parent ${parentId} disconnected, cleaning up`);
      args.peerConnectionsMap.delete(parentId);
    }

    args.updateConnectedPeersCount();
  };

  pc.oniceconnectionstatechange = () => {
    debugLog(`[WebRTC/Baby] ICE state for ${parentId}: ${pc.iceConnectionState}`);
  };

  pc.ontrack = (event) => {
    debugLog(
      `[WebRTC/Baby] Received track from parent ${parentId}: ${event.track.kind}`,
    );

    if (event.track.kind === "audio" && event.streams[0] && args.onParentAudioTrack) {
      debugLog("[WebRTC/Baby] Parent audio track received");
      args.onParentAudioTrack(event.track, event.streams[0]);
    }
  };

  return pc;
}

export function createParentPeerConnection(args: {
  setConnectionState: (state: ConnectionState) => void;
  onRemoteStream?: (stream: MediaStream) => void;
  onVideoTrackReceived?: () => void;
  onDisconnected: () => void;
}): RTCPeerConnection {
  debugLog("[WebRTC/Parent] Creating new RTCPeerConnection");
  const pc = new RTCPeerConnection(ICE_SERVERS);

  pc.onicecandidate = () => {
    // Handled in call() because it needs the current target id.
  };

  pc.onconnectionstatechange = () => {
    debugLog(`[WebRTC/Parent] Connection State: ${pc.connectionState}`);
    args.setConnectionState(pc.connectionState as ConnectionState);

    if (pc.connectionState === "connected") {
      setStreamingActive(true);
      return;
    }

    if (
      pc.connectionState === "disconnected" ||
      pc.connectionState === "failed" ||
      pc.connectionState === "closed"
    ) {
      debugLog("[WebRTC/Parent] Connection ended, resetting");
      setStreamingActive(false);
      args.onDisconnected();
    }
  };

  pc.oniceconnectionstatechange = () => {
    debugLog(`[WebRTC/Parent] ICE Connection State: ${pc.iceConnectionState}`);
  };

  pc.ontrack = (event) => {
    debugLog(`[WebRTC/Parent] Received track: ${event.track.kind}`);

    if (event.streams[0] && args.onRemoteStream) {
      args.onRemoteStream(event.streams[0]);
    }

    if (event.track.kind === "video" && args.onVideoTrackReceived) {
      debugLog("[WebRTC/Parent] Video track received - notifying");
      args.onVideoTrackReceived();
    }
  };

  return pc;
}

export function ensureReceiveOnlyTransceivers(pc: RTCPeerConnection): void {
  const existingTransceivers = pc.getTransceivers();
  const hasVideo = existingTransceivers.some(
    (transceiver) =>
      transceiver.receiver.track?.kind === "video" ||
      transceiver.sender.track?.kind === "video",
  );
  const hasAudio = existingTransceivers.some(
    (transceiver) =>
      transceiver.receiver.track?.kind === "audio" ||
      transceiver.sender.track?.kind === "audio",
  );

  if (!hasVideo) {
    debugLog("[WebRTC/Parent] Adding recvonly video transceiver");
    pc.addTransceiver("video", { direction: "recvonly" });
  }

  if (!hasAudio) {
    debugLog("[WebRTC/Parent] Adding recvonly audio transceiver");
    pc.addTransceiver("audio", { direction: "recvonly" });
  }
}
